import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate for redirection
import { motion } from "framer-motion";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import useAnimatedNavToggler from "../../helpers/useAnimatedNavToggler.js";
import logo from "../../images/logo.png";
import { ReactComponent as MenuIcon } from "feather-icons/dist/icons/menu.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { useTranslation } from 'react-i18next';

// Styled Components
const Header = tw.header`
  flex justify-between items-center max-w-screen-xl mx-auto mt-[30px] mb-[40px]
`;


export const NavLinks = tw.div`flex items-center gap-2	`;

export const NavLink = tw.a`
  text-lg my-2 lg:text-sm lg:mx-6 lg:my-0
  font-semibold tracking-wide transition duration-300
  pb-1 border-b-2 border-transparent hover:border-[#000000] hocus:text-[#000000]
`;


export const PrimaryLink = tw(NavLink)`
  lg:mx-0
  px-8 py-3 rounded bg-[#000000] text-gray-100
  hocus:bg-[#000000] hocus:text-gray-200 focus:shadow-outline
  border-b-0
`;


export const LogoLink = styled(NavLink)`
  ${tw`flex items-center font-black border-b-0 text-2xl! ml-0!`};

  img {
    width: 130px; /* Set your desired width */
    height: auto; /* Maintain aspect ratio */
    ${tw`mr-3`} /* Tailwind margin right */
  }
`;

const MobileNavLinksContainer = tw.nav`
  flex flex-1 items-center justify-between lg:hidden
`
export const NavToggle = tw.button`
  lg:hidden z-20 focus:outline-none hocus:text-primary-500 transition duration-300
`;
const MobileNavLinks = motion(styled.div`
  position: absolute;
  top: 170 px; // 👈 Reduced spacing
  right: 0;
  width: 16rem;
  padding: 1.5rem;
  background-color: white;
  color: #1f2937;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  z-index: 50;
`);
const MobileNavLinksList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1rem;
`;
export const DesktopNavLinks = tw.nav`
  hidden lg:flex flex-1 justify-between items-center
`;

const DropdownMenu = styled.div`
  ${tw`absolute mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5`};
  z-index: 10;
  right: 0;
  min-width: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  margin-right: ${({ isArabic }) => (isArabic ? '-100px' : '16px')};
`;


// Styled NavLink for Dropdown with Hover Color
export const NavLinkStyled = styled(NavLink)`
  ${tw`block px-4 py-2 text-gray-800 transition-colors duration-300`};
  &:hover {
    background-color: #000000; /* Ajoute le fond bleu personnalisé */
    color: white; /* Change la couleur du texte pour plus de visibilité */
  }
`;



export const UserButton = styled.button`
  ${tw`relative px-4 py-2 rounded-full bg-[#000000] text-white font-semibold mr-4 transition duration-300`};
  &:hover {
    background-color: #1e3d56; /* Nouvelle couleur au survol */
    color: #ffffff; /* Texte blanc */
  }
  &:focus {
    ${tw`outline-none`};
  }
`;


export default ({
  roundedHeaderButton = false,
  logoLink,
  links,
  className,
  collapseBreakpointClass = "lg",
}) => {
const { i18n, t } = useTranslation();

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { showNavLinks, animation, toggleNavbar } = useAnimatedNavToggler();
  const collapseBreakpointCss = collapseBreakPointCssMap[collapseBreakpointClass];

  // New state to hold user info
  const [user, setUser] = useState(null);

  const navigate = useNavigate(); // Use navigate for redirection

  // Check if user is logged in and retrieve info from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user'); // Get the user data from localStorage
    if (userData) {
      setUser(JSON.parse(userData)); // Parse and set the user data in state
    }
  }, []);

  // Function to handle logout
  const handleLogout = () => {
    localStorage.removeItem('user'); // Clear user data from localStorage
    localStorage.removeItem('authToken'); // Optionally remove authToken or other info
    localStorage.removeItem('id');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('role');

    setUser(null);
 // Reset the user state
        localStorage.setItem('role', "supplier"); // Store admin status

    navigate('/'); // Redirect to the home page
  };

  // Default navigation links
  const defaultLinks = [
    <NavLinks key={1}>
      <NavLink href="/produits-admin"> {t('Produits')}</NavLink>
      <NavLink href="/order-admin"> {t('Commandes')}</NavLink>
      <NavLink href="/retraite"> {t('Retraite')}</NavLink>
      <NavLink href="/payment-admin"> {t('Paiement')}</NavLink>
      <NavLink href="/user-admin"> {t('Utilisateurs')}</NavLink>
      <NavLink href="/client-admin"> {t('Clients')}</NavLink>


      {user ? (
        // When user is logged in, display user's name with dropdown for Profile & Logout
        <div tw="relative inline-block" >
          <UserButton onClick={() => setDropdownOpen(!dropdownOpen)}>
            {user.name} {/* Display the user's name */}
          </UserButton>
          {dropdownOpen && (
            <DropdownMenu isArabic={i18n.language === 'ar'} style={{ marginRight: '-30px' }}>
                       <NavLink
                href="/dashbord-admin"
                tw="block px-4 py-2 text-gray-800 hover:bg-gray-200"
              >
 {t('Tableau_de_bord')}
              </NavLink>
                <NavLink
                href="/admin-announcement"
                tw="block px-4 py-2 text-gray-800 hover:bg-gray-200"
              >
 {t('Announcement')}
              </NavLink>
              <NavLink
                href="/"
                onClick={handleLogout} // Call the handleLogout function on click
                tw="block px-4 py-2 text-gray-800 hover:bg-gray-200"
              >
                 {t('Déconnexion')}
              </NavLink>
            </DropdownMenu>
          )}
        </div>
      ) : (
        // If user is not logged in, display Login and Sign Up
        <>
          {/* <NavLink href="/login" tw="lg:ml-12!">
            Connexion
          </NavLink>
          <PrimaryLink css={roundedHeaderButton && tw`rounded-full`} href="/signup">
            Inscription
          </PrimaryLink> */}
        </>
      )}
    </NavLinks>,
  ];

  const defaultLogoLink = (
    <LogoLink href="/">
<img src={logo} alt="logo" width={1000} height={1000} />
</LogoLink>
  );

  logoLink = logoLink || defaultLogoLink;
  links = links || defaultLinks;

  return (
    <Header className={className || "header-light"}>
      <DesktopNavLinks css={collapseBreakpointCss.desktopNavLinks}>
        {logoLink}
        {links}
      </DesktopNavLinks>

      <MobileNavLinksContainer css={collapseBreakpointCss.mobileNavLinksContainer}>
        {logoLink}
        <MobileNavLinks
          initial={{ x: "150%", display: "none" }}
          animate={animation}
          css={collapseBreakpointCss.mobileNavLinks}
        >
          {links}
        </MobileNavLinks>
        <NavToggle onClick={toggleNavbar} className={showNavLinks ? "open" : "closed"}>
          {showNavLinks ? <CloseIcon tw="w-6 h-6" /> : <MenuIcon tw="w-6 h-6" />}
        </NavToggle>
      </MobileNavLinksContainer>
    </Header>
  );
};

// Breakpoint mappings for different screen sizes
const collapseBreakPointCssMap = {
  sm: {
    mobileNavLinks: tw`sm:hidden`,
    desktopNavLinks: tw`sm:flex`,
    mobileNavLinksContainer: tw`sm:hidden`,
  },
  md: {
    mobileNavLinks: tw`md:hidden`,
    desktopNavLinks: tw`md:flex`,
    mobileNavLinksContainer: tw`md:hidden`,
  },
  lg: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
  xl: {
    mobileNavLinks: tw`lg:hidden`,
    desktopNavLinks: tw`lg:flex`,
    mobileNavLinksContainer: tw`lg:hidden`,
  },
};
